import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { trackSignUp } from "@/lib/analytics";
import { ArrowRight } from "@phosphor-icons/react";

const LOCKUP = "https://customer-assets-rejwkqb3.emergentagent.net/job_proposal-scorecard/artifacts/mxac6slj_proposalforensics.png";
const inputCls = "mt-2 w-full h-11 px-3.5 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await register(form);
      trackSignUp("email");  // fire exactly once, only after successful account creation
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-black grain flex-col justify-between p-14 text-white">
        <img src={LOCKUP} alt="Proposal Forensics" className="w-full max-w-sm -ml-2" />
        <div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-[1.05]">Win the right work<br />at the right price.</h1>
          <p className="mt-6 text-slate-400 max-w-md text-base leading-relaxed">Create your company account. Proposal Forensics tailors every analysis to your rates, standards and geographic market.</p>
        </div>
        <p className="font-mono-plex text-[11px] uppercase tracking-[0.25em] text-slate-500">Analyze · Optimize · Approve</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-[#FAFAFA] grain overflow-y-auto">
        <div className="w-full max-w-sm py-10">
          <div className="mb-8">
            <h2 className="font-display font-extrabold text-3xl tracking-tight text-[#0F172A]">Create account</h2>
            <p className="text-slate-500 mt-2 text-sm">Start with your company &amp; admin login. You'll add details next.</p>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Company Name</label>
              <input data-testid="register-company-input" value={form.company_name} onChange={set("company_name")} required placeholder="Apex AV Integration" className={inputCls} />
            </div>
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Your Name</label>
              <input data-testid="register-name-input" value={form.name} onChange={set("name")} required placeholder="Jordan Smith" className={inputCls} />
            </div>
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Work Email</label>
              <input data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} required placeholder="you@company.com" className={inputCls} />
            </div>
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Password</label>
              <input data-testid="register-password-input" type="password" value={form.password} onChange={set("password")} required placeholder="At least 8 characters" className={inputCls} />
            </div>
            {error && <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
            <button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {loading ? "Creating…" : "Create account"}{!loading && <ArrowRight size={16} weight="bold" />}
            </button>
          </form>
          <p className="mt-6 text-sm text-slate-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-[#0F172A] hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
